"use client";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import stripeLogo from "../../assets/stripe.png";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { trackEvent } from "../../analytics";

const API_URL = `${REST_API_URL}/payment/payment-plans/`;

export default function Payment() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNotice, setShowNotice] = useState(false);

  const [isSwitch, setIsSwitch] = useState(false);
  const [paymentPlanIndex, setPaymentPlanIndex] = useState(0);

  const getPlans = async () => {
    try {
      setLoading(true);
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      const headers = buildHeaders();

      const res = await fetch(API_URL, {
        headers,
      });

      const data = await res.json();
      setPlans(Array.isArray(data) ? data : []);
    } catch (e) {
      console.log("Failed to load plans", e);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setOpen(true);
    getPlans();
  };

  const filteredByPeriod = plans.filter(
    (p) => p.period === (isSwitch ? "Yearly" : "Monthly"),
  );

  const currentUsage = filteredByPeriod[0]?.usage;
  const filteredPlans = filteredByPeriod.filter(
    (p) => p.usage === currentUsage,
  );
  const selectedPlan = filteredPlans[paymentPlanIndex] ?? null;

  return (
    <>
      <div className="min-h-screen w-full flex flex-col items-center justify-center text-center relative overflow-hidden bg-[#eef0f8]">
        <div className="absolute inset-0 blur-[120px] opacity-40 bg-gradient-to-br from-[#020637] via-purple-400 to-pink-400"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,white_0%,#eef1ff_100%)]"></div>

        <img
          src={stripeLogo}
          className="w-full max-w-[320px] sm:max-w-[272px] drop-shadow-xl animate-fadeInUp relative z-10 opacity-95"
          alt="stripe"
        />

        <button
          onClick={() => {
            if (typeof window.gtag === "function") {
              window.gtag("event", "begin_checkout", {
                currency: "USD",
                items: [{ item_name: "Payment plans" }],
              });
            }
            openModal();
          }}
          className="mt-10 px-16 py-4 text-white text-lg bg-[#020637] hover:bg-[#0e1c7a] rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 relative z-10"
        >
          {t("home.connectButton")}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-center items-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="bg-[#EDEEF2] w-[92vw] max-w-[560px] rounded-3xl p-8 relative shadow-[0_0_45px_rgba(0,0,0,0.12)] border border-gray-100"
            >
              <button
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-900 text-2xl"
                onClick={() => setOpen(false)}
              >
                ×
              </button>

              <h2 className="text-center text-2xl font-semibold text-gray-900 mb-8">
                {t("home.modalTitle")}
              </h2>

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-4 border-[#020637] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <motion.div
                    className="flex justify-center mb-6 bg-[#EDEEF2] rounded-full p-1 relative"
                    layout
                  >
                    <motion.div
                      layout
                      transition={{
                        type: "spring",
                        stiffness: 220,
                        damping: 18,
                      }}
                      className="absolute top-1 bottom-1 w-1/2 rounded-full bg-[#020637]"
                      style={{ left: isSwitch ? "50%" : "0%" }}
                    />

                    <motion.button
                      whileTap={{ scale: 0.94 }}
                      className={`z-10 w-1/2 py-2 text-sm font-medium transition ${
                        !isSwitch ? "text-white" : "text-gray-600"
                      }`}
                      onClick={() => {
                        setIsSwitch(false);
                        setPaymentPlanIndex(0);
                      }}
                    >
                      {t("home.tabMonthly")}
                    </motion.button>

                    <motion.button
                      whileTap={{ scale: 0.94 }}
                      className={`z-10 w-1/2 py-2 text-sm font-medium transition ${
                        isSwitch ? "text-white" : "text-gray-600"
                      }`}
                      onClick={() => {
                        setIsSwitch(true);
                        setPaymentPlanIndex(0);
                      }}
                    >
                      {t("home.tabYearly")}
                    </motion.button>
                  </motion.div>

                  <Box sx={{ width: "100%", mt: 2, mb: 4 }}>
                    <Stepper
                      activeStep={paymentPlanIndex}
                      alternativeLabel
                      sx={{
                        ".MuiStepLabel-label": {
                          color: "#6b7280",
                          fontSize: "0.9rem",
                        },
                        ".Mui-active .MuiStepLabel-label": {
                          color: "#020637 !important",
                          fontWeight: 600,
                        },
                        ".Mui-completed .MuiStepLabel-label": {
                          color: "#020637",
                        },
                        ".MuiStepIcon-root": { color: "#cdd0e3" },
                        ".Mui-active .MuiStepIcon-root": {
                          color: "#020637 !important",
                        },
                        ".Mui-completed .MuiStepIcon-root": {
                          color: "#020637 !important",
                        },
                      }}
                    >
                      {filteredPlans.map((plan, index) => (
                        <Step key={plan.id}>
                          <motion.div
                            whileHover={{ scale: 1.12 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setPaymentPlanIndex(index)}
                            style={{ cursor: "pointer" }}
                            transition={{
                              type: "spring",
                              stiffness: 260,
                              damping: 16,
                            }}
                          >
                            <StepLabel>{plan.name}</StepLabel>
                          </motion.div>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>

                  <AnimatePresence mode="wait">
                    {selectedPlan && (
                      <motion.div
                        key={selectedPlan.id}
                        initial={{ opacity: 0, y: 20, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.98 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="mt-10 flex flex-col sm:flex-row gap-8 items-center"
                      >
                        <div className="flex-1 flex justify-center items-center min-h-[260px]">
                          <img
                            src={selectedPlan.image}
                            className="w-full h-full object-contain drop-shadow-xl"
                            alt=""
                          />
                        </div>

                        <div className="flex-1 text-center sm:text-left">
                          <h3 className="text-2xl font-semibold text-gray-900 mb-1 text-start">
                            {selectedPlan.name}
                          </h3>

                          <p className="text-[#020637] font-bold text-3xl mb-3 text-start">
                            ${selectedPlan.price}
                          </p>

                          <p className="text-gray-600 text-sm mb-5 leading-relaxed max-w-xs sm:max-w-none mx-auto sm:mx-0 text-start">
                            {selectedPlan.description}
                          </p>

                          <ul className="text-gray-700 text-sm space-y-1 mb-6">
                            {(selectedPlan.includs ?? []).map((i) => (
                              <li key={i.id} className="flex items-center">
                                <span className="text-[#020637] mr-2">•</span>{" "}
                                {i.text}
                              </li>
                            ))}
                          </ul>

                          <button
                            className="w-full cursor-pointer px-10 py-3 bg-[#020637] hover:bg-[#0e1c7a] text-white rounded-xl transition font-medium shadow-md hover:shadow-lg"
                            onClick={() => {
                              trackEvent("checkout_started", {
                                plan_name: selectedPlan?.name,
                                price: Number(selectedPlan?.price),
                              });
                              if (
                                typeof window.gtag === "function" &&
                                selectedPlan
                              ) {
                                window.gtag("event", "begin_checkout", {
                                  currency: "USD",
                                  value: Number(selectedPlan.price),
                                  items: [
                                    {
                                      item_id: String(selectedPlan.id),
                                      item_name: selectedPlan.name,
                                      price: Number(selectedPlan.price),
                                      quantity: 1,
                                    },
                                  ],
                                });
                              }
                              setShowNotice(true);
                            }}
                          >
                            {t("home.continueButton")}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showNotice && (
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex justify-center items-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              transition={{ duration: 0.25 }}
              className="bg-[#EDEEF2] max-w-md w-full rounded-2xl shadow-xl p-6 text-center"
            >
              <h3 className="text-lg font-semibold mb-4">
                {t("home.noticeTitle")}
              </h3>

              <p className="text-gray-700 text-sm leading-relaxed mb-6 whitespace-pre-line">
                {t("home.noticeMessage")}
              </p>

              <button
                onClick={() => {
                  if (typeof window.gtag === "function") {
                    window.gtag("event", "paid_error");
                  }
                  setShowNotice(false);
                }}
                className="mt-2 px-10 py-2 bg-[#020637] hover:bg-[#0e1c7a] text-white rounded-lg shadow transition"
              >
                {t("home.okButton")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
